import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import QRCode from "react-qr-code";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import axios from "axios";
import {
  Mail, Phone, MapPin, Briefcase, Globe, Linkedin, 
  QrCode, Share2, Download, MessageCircle, ArrowLeft, Copy,
  Instagram, Facebook, Youtube, Apple
} from "lucide-react";
import { toast } from "sonner";

const PublicProfile = () => {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQR, setShowQR] = useState(false);

  const profileUrl = window.location.href;

  const isAppleDevice = typeof navigator !== "undefined" && (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
  const walletUrl = `${API}/users/${slug}/wallet-pass?base=${encodeURIComponent(window.location.origin)}`;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/users/profile/${slug}`);
        setProfile(response.data.user);
        setCompany(response.data.company);
      } catch (err) {
        setError(err.response?.data?.detail || "Profile not found");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [slug]);

  // Apply theme
  useEffect(() => {
    if (profile) {
      const theme = company?.theme || profile.theme || "light";
      document.documentElement.classList.remove("light", "dark");
      document.documentElement.classList.add(theme);
    }
    return () => {
      document.documentElement.classList.remove("light", "dark");
      document.documentElement.classList.add("light");
    };
  }, [profile, company]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(profileUrl);
    toast.success("Profile link copied!");
  };

  const shareProfile = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile.name} - Digital Business Card`,
          text: `Check out ${profile.name}'s profile`,
          url: profileUrl
        });
      } catch (err) {
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const downloadVCard = () => {
    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${profile.name}
TITLE:${profile.profession || ""}
TEL:${profile.phone || ""}
EMAIL:${profile.email_public || ""}
URL:${profile.website || ""}
NOTE:${profile.bio || ""}
END:VCARD`;

    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${profile.name.replace(/\s+/g, "_")}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Contact downloaded!");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
        <h1 className="text-2xl font-bold mb-2">Profile Not Available</h1>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Link to="/">
          <Button>
            <ArrowLeft className="h-4 w-4 mr-2" /> Go Home
          </Button>
        </Link>
      </div>
    );
  }

  const primaryColor = company?.primary_color || "#4F46E5";
  const secondaryColor = company?.secondary_color || "#10B981";

  return (
    <div className="min-h-screen bg-background">
      <div className="mobile-container bg-background">
        {/* Header with gradient - white for company users */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`relative flex flex-col items-center ${company ? 'pt-6 pb-4' : 'h-40 justify-center'}`}
          style={{ 
            background: company ? '#FFFFFF' : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}CC 100%)`
          }}
        >
          {/* Company Logo - positioned at top for company users */}
          {company?.logo_url && (
            <img 
              src={company.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${company.logo_url}` : company.logo_url}
              alt={company.name}
              className="h-12 w-auto mb-4"
            />
          )}
          
          {/* Avatar - inside header for company users to avoid overlap */}
          {company && (
            <Avatar className="h-28 w-28 border-4 border-background shadow-xl">
              <AvatarImage 
                src={profile.photo_url ? (profile.photo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo_url}` : profile.photo_url) : undefined}
              />
              <AvatarFallback className="text-4xl bg-muted">{profile.name?.charAt(0) || "?"}</AvatarFallback>
            </Avatar>
          )}
        </motion.div>

        {/* Profile Content */}
        <div className={`px-6 pb-8 relative ${company ? 'pt-4' : '-mt-16'}`}>
          {/* Avatar - only for individual users (overlapping header) */}
          {!company && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="flex justify-center mb-4"
            >
              <Avatar className="h-32 w-32 border-4 border-background shadow-xl">
                <AvatarImage 
                  src={profile.photo_url ? (profile.photo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo_url}` : profile.photo_url) : undefined}
                />
                <AvatarFallback className="text-4xl bg-muted">{profile.name?.charAt(0) || "?"}</AvatarFallback>
              </Avatar>
            </motion.div>
          )}

          {/* Name & Profession */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-6"
          >
            <h1 className="text-2xl font-bold">{profile.name}</h1>
            {profile.profession && (
              <p className="text-lg text-muted-foreground">{profile.profession}</p>
            )}
            {profile.profession_details && (
              <p className="text-sm text-muted-foreground mt-1">{profile.profession_details}</p>
            )}
            {profile.location && (
              <p className="text-sm text-muted-foreground mt-2 flex items-center justify-center gap-1">
                <MapPin className="h-3 w-3" /> {profile.location}
              </p>
            )}
          </motion.div>

          {/* Bio */}
          {profile.bio && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.25 }}
              className="text-center mb-6"
            >
              <p className="text-sm text-muted-foreground">{profile.bio}</p>
            </motion.div>
          )}

          {/* Quick Actions */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-3 gap-3 mb-6"
          >
            <Dialog open={showQR} onOpenChange={setShowQR}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="h-auto py-3 flex-col gap-1"
                  data-testid="public-qr-btn"
                >
                  <QrCode className="h-5 w-5" />
                  <span className="text-xs">QR Code</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle className="text-center">Scan to Connect</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center py-6">
                  <div className="qr-container">
                    <QRCode value={profileUrl} size={200} />
                  </div>
                  <Button variant="outline" className="mt-4" onClick={copyToClipboard}>
                    <Copy className="h-4 w-4 mr-2" /> Copy Link
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Button 
              variant="outline" 
              className="h-auto py-3 flex-col gap-1"
              onClick={shareProfile}
              data-testid="public-share-btn"
            >
              <Share2 className="h-5 w-5" />
              <span className="text-xs">Share</span>
            </Button>

            <Button 
              variant="outline" 
              className="h-auto py-3 flex-col gap-1"
              onClick={downloadVCard}
              data-testid="public-download-btn"
            >
              <Download className="h-5 w-5" />
              <span className="text-xs">Save</span>
            </Button>
          </motion.div>

          {/* Add to Apple Wallet */}
          {isAppleDevice ? (
            <motion.a
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.35 }}
              href={walletUrl}
              className="mb-2 flex items-center justify-center gap-2 w-full rounded-xl bg-black text-white py-3.5 text-sm font-semibold hover:bg-zinc-800 transition-colors"
              data-testid="add-to-wallet-btn"
            >
              <Apple className="h-5 w-5" fill="currentColor" />
              Add to Apple Wallet
            </motion.a>
          ) : (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.button
                    type="button"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.35 }}
                    onClick={() => toast("Open this profile on an iPhone in Safari to add it to Apple Wallet")}
                    className="mb-2 flex items-center justify-center gap-2 w-full rounded-xl bg-black/50 text-white/80 py-3.5 text-sm font-semibold cursor-not-allowed"
                    data-testid="add-to-wallet-btn"
                  >
                    <Apple className="h-5 w-5" fill="currentColor" />
                    Add to Apple Wallet
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent>
                  Open on an iPhone (Safari) to add to Apple Wallet
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Contact Cards */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="space-y-3"
          >
            {profile.email_public && (
              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => window.location.href = `mailto:${profile.email_public}`}
                data-testid="contact-email"
              >
                <CardContent className="py-3 px-4 flex items-center gap-4">
                  <div 
                    className="h-10 w-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: primaryColor + "20" }}
                  >
                    <Mail className="h-5 w-5" style={{ color: primaryColor }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="font-medium truncate">{profile.email_public}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {profile.phone && (
              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => window.location.href = `tel:${profile.phone}`}
                data-testid="contact-phone"
              >
                <CardContent className="py-3 px-4 flex items-center gap-4">
                  <div 
                    className="h-10 w-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: primaryColor + "20" }}
                  >
                    <Phone className="h-5 w-5" style={{ color: primaryColor }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">Phone</p>
                    <p className="font-medium">{profile.phone}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {profile.whatsapp && (
              <Card 
                className="cursor-pointer hover:border-green-500/50 transition-colors"
                onClick={() => window.open(`https://wa.me/${profile.whatsapp.replace(/[^0-9]/g, "")}`, "_blank")}
                data-testid="contact-whatsapp"
              >
                <CardContent className="py-3 px-4 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-green-500/20">
                    <MessageCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">WhatsApp</p>
                    <p className="font-medium">{profile.whatsapp}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {profile.linkedin && (
              <Card 
                className="cursor-pointer hover:border-blue-500/50 transition-colors"
                onClick={() => window.open(profile.linkedin, "_blank")}
                data-testid="contact-linkedin"
              >
                <CardContent className="py-3 px-4 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-blue-500/20">
                    <Linkedin className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">LinkedIn</p>
                    <p className="font-medium truncate">View Profile</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {profile.website && (
              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => window.open(profile.website, "_blank")}
                data-testid="contact-website"
              >
                <CardContent className="py-3 px-4 flex items-center gap-4">
                  <div 
                    className="h-10 w-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: secondaryColor + "20" }}
                  >
                    <Globe className="h-5 w-5" style={{ color: secondaryColor }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">Website</p>
                    <p className="font-medium truncate">{profile.website.replace(/^https?:\/\//, "")}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Instagram */}
            {profile.instagram && (
              <Card 
                className="cursor-pointer hover:border-pink-500/50 transition-colors"
                onClick={() => window.open(profile.instagram, "_blank")}
                data-testid="contact-instagram"
              >
                <CardContent className="py-3 px-4 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-purple-500/20 to-pink-500/20">
                    <Instagram className="h-5 w-5 text-pink-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">Instagram</p>
                    <p className="font-medium truncate">Follow</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Facebook */}
            {profile.facebook && (
              <Card 
                className="cursor-pointer hover:border-blue-600/50 transition-colors"
                onClick={() => window.open(profile.facebook, "_blank")}
                data-testid="contact-facebook"
              >
                <CardContent className="py-3 px-4 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-blue-600/20">
                    <Facebook className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">Facebook</p>
                    <p className="font-medium truncate">Connect</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* X (Twitter) */}
            {profile.twitter && (
              <Card 
                className="cursor-pointer hover:border-gray-500/50 transition-colors"
                onClick={() => window.open(profile.twitter, "_blank")}
                data-testid="contact-twitter"
              >
                <CardContent className="py-3 px-4 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-gray-900/20">
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">X (Twitter)</p>
                    <p className="font-medium truncate">Follow</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* TikTok */}
            {profile.tiktok && (
              <Card 
                className="cursor-pointer hover:border-gray-500/50 transition-colors"
                onClick={() => window.open(profile.tiktok, "_blank")}
                data-testid="contact-tiktok"
              >
                <CardContent className="py-3 px-4 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-gray-900/20">
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">TikTok</p>
                    <p className="font-medium truncate">Follow</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Snapchat */}
            {profile.snapchat && (
              <Card 
                className="cursor-pointer hover:border-yellow-500/50 transition-colors"
                onClick={() => window.open(profile.snapchat, "_blank")}
                data-testid="contact-snapchat"
              >
                <CardContent className="py-3 px-4 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-yellow-400/20">
                    <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105l-.046-.569c-.098-1.626-.225-3.651.307-4.837C7.392 1.077 10.739.807 11.727.807l.419-.015h.06z"/>
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">Snapchat</p>
                    <p className="font-medium truncate">Add Me</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* YouTube */}
            {profile.youtube && (
              <Card 
                className="cursor-pointer hover:border-red-500/50 transition-colors"
                onClick={() => window.open(profile.youtube, "_blank")}
                data-testid="contact-youtube"
              >
                <CardContent className="py-3 px-4 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-red-500/20">
                    <Youtube className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">YouTube</p>
                    <p className="font-medium truncate">Subscribe</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-8 pt-6 border-t"
          >
            <p className="text-xs text-muted-foreground">
              Powered by h75c
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
